import {
  clientName,
  scenarios,
  FilesAnalyticsScenarios
} from "@msteams/bridges-models-files";

import {
  pageLoadConfigurationFacet,
  telemetryHandlersConfigurationFacet,
  IItemCacheConfiguration,
  pruningConfigurationFacet,
  localeFacet,
  backgroundTaskQueueFacet,
  AsyncBackgroundTaskQueue,
  enableReadOnlyConfigurationFacet,
  enableFluentIconsFacet,
  enableListLoadingSpinnerFacet,
  disableShimmerBeforePageLoadConfigurationFacet,
  disableSkipViewportMeasureConfigurationFacet
} from "@ms/items-view-prefetch";

import {
  spDataSourceConfigurationFacet,
  spPrefetchDataConfigurationFacet
} from "@ms/items-view-addon-sp-prefetch";
import {
  BackgroundTaskRunner,
  operationHandlerConfigurationFacet // Only present in items-view
} from "@ms/items-view";

import { IEventData, ITypedScenario } from "@msteams/bridges-models-ts-logging";
import { IAnalyticsService } from "@msteams/bridges-models-ts-analytics";

import {
  AsyncTelemetryHandler,
  WebSharedAriaTelemetryHandler
} from "@ms/telemetry-addon-aria";
import { graphDataSourceConfigurationFacet } from "@ms/items-view-addon-graph";
import { getLoggerFacet } from "./services-files-telemetry/items-view-logging";
import { getErrorHandlerFacet } from "./services-files-telemetry/items-view-error-handler";
import { ItemsViewTelemetryHandlerFactory } from "./services-files-telemetry/items-view-telemetry-handler-factory";
import { teamsToItemsViewLocaleMap } from "./constants";
import { LocalizationService } from "@msteams/bridges-services-ts-localization";
import { getDomainFromSiteUrl } from "@msteams/bridges-services-utilities-files";
import {
  getPrefetchListData,
  listDataUpdateCallback
} from "./files-prefetch-list-data";
import { LoggingService } from "@msteams/bridges-services-ts-logging";

const TWO = 2;
const TEN = 10;
const SIXTY = 60;
const THOUSAND = 1000;
const MAX_AGE = TEN * SIXTY * THOUSAND; // 10 mintues
// eslint-disable-next-line max-lines-per-function
export function getItemsViewConfigurationFacet(
  scenario: ITypedScenario<IEventData>,
  authUrl: string,
  ensureAuthToken: (url: string) => Promise<string>,
  analyticsService?: IAnalyticsService<FilesAnalyticsScenarios>,
  useCachedPrefetch?: boolean,
  createFolderAndRefreshSiteUrl?: (threadId: string) => void
): IItemCacheConfiguration {
  // This logger handles the render completion event of item-scope, to close the bridge scenario.
  const logger = getLoggerFacet(scenario); // This depends on itmes-view. Cannot prefecth this. IFaceted is missing
  const errorHandler = getErrorHandlerFacet();
  const spDataSourceConfiguration = spDataSourceConfigurationFacet.pack({
    ensureAuthToken
  });
  ItemsViewTelemetryHandlerFactory.init(
    analyticsService!!!,
    createFolderAndRefreshSiteUrl
  );
  const itemsViewTelemetryHandler = ItemsViewTelemetryHandlerFactory.getItemsViewTelemetryHandler();
  const configuration: IItemCacheConfiguration = {
    ...spDataSourceConfiguration,
    ...logger,
    ...errorHandler,
    ...telemetryHandlersConfigurationFacet.pack([
      new AsyncTelemetryHandler(async () => {
        // Accquire auth token for any URL
        const userAuthToken = await ensureAuthToken(authUrl);
        return new WebSharedAriaTelemetryHandler({
          clientName,
          scenario: scenarios.files, // Common scenario is files
          ariaEnvironment: "prod",
          userAuthToken,
          correlationId:
            LoggingService.getSessionId && LoggingService.getSessionId()
        });
      }),
      itemsViewTelemetryHandler
    ]),
    // enablePageLoadBarrier deferes loading the addons after the page has loaded
    ...pageLoadConfigurationFacet.pack({
      enablePageLoadBarrier: true
    }),
    // The page durarion default is 10 minutes
    ...pruningConfigurationFacet.pack({
      maxAge: MAX_AGE // Keep last-accessed items around for this max age
    }),
    ...localeFacet.pack({
      language: convertTeamsToItemsViewLocale(LocalizationService.getLocale())
    }),
    // Allows itemsView to run tasks in the background
    ...backgroundTaskQueueFacet.pack(
      new AsyncBackgroundTaskQueue(async () => new BackgroundTaskRunner())
    ),
    ...enableFluentIconsFacet.pack(true),
    ...graphDataSourceConfigurationFacet.pack({
      ensureAuthToken: (_scopes: string[], endpoint: string) => {
        const url = getDomainFromSiteUrl(endpoint);
        return ensureAuthToken(url);
      }
    }),
    ...enableReadOnlyConfigurationFacet.pack(true),
    ...(useCachedPrefetch ? getItemsViewPrefetchConfiguration() : {}),
    ...enableListLoadingSpinnerFacet.pack(true),
    ...disableShimmerBeforePageLoadConfigurationFacet.pack(false),
    ...disableSkipViewportMeasureConfigurationFacet.pack(false),
    ...operationHandlerConfigurationFacet.pack({
      enableTimeout: true,
      defaultTimeout: TWO * SIXTY * THOUSAND // 2 minute timeouts, same as scenario default timeouts
    })
  };
  return configuration;
}

function convertTeamsToItemsViewLocale(teamsLocale: string): string {
  const itemViewLocale = teamsToItemsViewLocaleMap[teamsLocale];
  if (!itemViewLocale) {
    // No mapping for teams locale found in items-view, fallback to en-us
    return "en-us";
  }
  return itemViewLocale;
}

function getItemsViewPrefetchConfiguration() {
  return spPrefetchDataConfigurationFacet.pack({
    prefetchData: getPrefetchListData,
    listDataUpdateCallback
  });
}
